import { logError } from "../../../utils/appUtils";
import { CarrierType } from "../../../utils/constants";
import { sessionYardRunsDriverCheckIn } from "../../../utils/gateAccess";
import {
  cancelGateCheckIn,
  commitStagedGateEdit,
  createManualGateCheckIn,
  GATE_TERMINAL_STATUSES,
  GateCheckInConflictError,
  GateLockNotClaimedError,
  GateDepartureNotDockedError,
  GateDepartureWorkPendingError,
  GateQueueOrderError,
} from "./gateCheckInUtils";
import { GatePlacementBlockedError } from "./gatePlacement";

/** The gate work both shipment dialogs do once their save has committed, in the
 * order they must do it. Inbound and outbound ran byte-identical copies of this
 * — same three blocks, same warnings, differing only in the log label and the
 * direction flag — so every gate fix had to land twice and be reviewed twice.
 * It lives here rather than inside `gateCheckInUtils` because it is caller
 * policy, not gate mechanics: what the manager is told, and what is best effort
 * versus what fails the save.
 *
 * Takes plain values, not the refs the dialogs hold them in, so it can be
 * tested without rendering a React Admin form. The caller reads each ref and
 * clears it before handing the value over.
 *
 * Never throws. Each block is best effort and warns on its own: the shipment
 * write has already landed by the time this runs, and no gate row is worth
 * telling a manager their save failed when it did not.
 *
 * @param {boolean} outbound which dialog is calling — picks the log label and
 *   the direction `createManualGateCheckIn` mints for
 * @param {function} notify React Admin's notify, for the warnings above
 * @param {object|false} strandedCheckIn the check-in left behind by a rolled
 *   back arrival, to retire now that the shipment's lock is clear
 * @param {{target: object, gateEdit: object}|false} stagedGateEdit the gate
 *   status change staged in transform, to commit now that the shipment is in
 * @param {object|false} savedShipment the shipment to mint a manual check-in
 *   for, if it turns out to want one
 * @param {object|false} manualGateOpening the driver, plate, dock and status
 *   the office typed into the gate control while that row was still pending —
 *   the row opens carrying them. Distinct from stagedGateEdit, which edits a
 *   row that already exists; these two are never both set for one save.
 */
export async function runGateSideEffects({
  outbound,
  notify,
  strandedCheckIn,
  stagedGateEdit,
  savedShipment,
  manualGateOpening,
}) {
  const logLabel = outbound
    ? "outboundShipment:DialogSaveButton"
    : "inboundShipments:DialogSaveButton";

  // The rolled-back shipment is committed and its lock cleared — now retire
  // the stranded check-in row. Awaited so a failure's warning lands before
  // the caller's redirect tears the form down.
  if (strandedCheckIn) {
    try {
      await cancelGateCheckIn(strandedCheckIn);
    } catch (err) {
      logError(`${logLabel}:cancelGateCheckIn`, err, false);
      notify(
        "Arrival rolled back, but the driver's gate check-in could not be cancelled — cancel it from the Gate Queue",
        "warning",
      );
    }
  }

  // The shipment write is in — commit the staged gate edit. Deferred to
  // here so a save that failed after transform never advanced the gate row
  // for a shipment that was not saved. Awaited so a failure's warning lands
  // before the caller's redirect tears the form down.
  let stagedGateEditWasTerminal = false;
  if (stagedGateEdit) {
    const { target, gateEdit } = stagedGateEdit;
    try {
      await commitStagedGateEdit(target, gateEdit);
      // Only an edit that landed suppresses the mint below. A failed commit
      // leaves the row active while this save has already released the lock
      // — the orphan the adopt path exists to reclaim, and suppressing on
      // the intent alone would leave it for the driver API to duplicate.
      stagedGateEditWasTerminal = GATE_TERMINAL_STATUSES.includes(
        gateEdit.status,
      );
    } catch (err) {
      // Both of these carry a message written for the user: the queue moved
      // between the pre-commit check and this write, or the row cannot end as
      // DEPARTED from where it stands. Neither is a fault to log.
      // A DOCKED edit the shipment cannot carry (the form refused the placed
      // stamp, so the save left it unplaced) is refused at the write for the
      // same reason, with the same message.
      const refused =
        err instanceof GateQueueOrderError ||
        err instanceof GateDepartureNotDockedError ||
        err instanceof GateDepartureWorkPendingError ||
        err instanceof GatePlacementBlockedError;
      if (!refused && !(err instanceof GateCheckInConflictError)) {
        logError(`${logLabel}:commitStagedGateEdit`, err, false);
      }
      notify(
        refused
          ? `Shipment saved, but the gate status change was not applied — ${err.message}.`
          : "Shipment saved, but the gate status change was not applied — the check-in changed at the gate. Review it on the Gate Queue.",
        "warning",
      );
    }
  }

  // A truck the office waved in gets the gate row the kiosk would have
  // written, so the crew work every truck the same way. createManualGateCheckIn
  // decides whether this shipment wants one; the yard and the carrier type are
  // tested here because they are what makes the gate apply at all. Best effort
  // and after the shipment is in: the arrival itself is saved either way, and
  // failing the save over a convenience row would cost the manager their work.
  //
  // Never on the same save as a terminal staged edit: that edit just ended
  // this visit's row and released the lock, and the snapshot it left behind
  // — an arrival, no placement, no gateCheckInId — is exactly what
  // createManualGateCheckIn reads as a truck wanting a row. It carries the
  // durable test itself, off the shipment's own check-in history, which is
  // what covers every later save and every status set from the Gate Queue
  // page; this flag covers the one case that history cannot answer yet,
  // where gateCheckInByShipment is a GSI and the row ended a moment ago
  // still reads active.
  if (
    savedShipment &&
    !stagedGateEditWasTerminal &&
    sessionYardRunsDriverCheckIn() &&
    savedShipment.carrierType === CarrierType.TRUCK
  ) {
    try {
      await createManualGateCheckIn(savedShipment, outbound, manualGateOpening);
    } catch (err) {
      logError(`${logLabel}:createManualGateCheckIn`, err, false);
      notify(
        err instanceof GateLockNotClaimedError
          ? // The row exists and the crew can see it. What is missing is the
            // shipment's pointer at it, which is what keeps a driver from
            // checking the same truck in a second time. Saving again is the
            // remedy, not a workaround: createManualGateCheckIn adopts an
            // active row on an unlocked shipment and claims the lock for it.
            "Shipment saved and the truck is on the Gate Queue, but it could not be linked to this shipment — save the shipment again to link it"
          : "Shipment saved, but the gate check-in for this truck was not created — the crew will not see it on the Gate Queue",
        "warning",
      );
    }
  }
}
